import { RouteFactory } from "@app/routePaths";
import { AdvertisementTypeEnum } from "@app/types/enums";
import { BlockTitle } from "@blocks/Blocks";
import Advertisement from "@components/Advertisement/Advertisement";
import PrestitialAdWrapper from "@components/Advertisement/PrestitialAdWrapper";
import Container from "@components/Container/Container";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import Header from "@components/Header/Header";
import MetaTags from "@components/MetaTags/MetaTags";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { cn, GoogleAdsSlotFactory } from "@util/helpers";

import HubBrandedContentArticles from "./components/HubBrandedContentArticles";
import HubPropertySupplementsArticles from "./components/HubPropertySupplementsArticles";
import HubSponsoredContentArticles from "./components/HubSponsoredContentArticles";
import HubWealthSupplementsArticles from "./components/HubWealthSupplementsArticles";
import { HubContext } from "./utils/types";

export type HubPageContentProps = Pick<HubContext, "data">;
export default function HubPageContent({
  data: {
    title,
    brandedContentArticles,
    propertySupplementsArticles,
    sponsoredContentArticles,
    wealthSupplementsArticles,
  },
}: HubPageContentProps): React.ReactElement {
  const { isPrestitialEnabled, topOverlayImpressions, topOverlayValidity } =
    useGlobalAdSetting();

  const adSlotPath = RouteFactory.hub.replace("/", "");

  return (
    <div className="w-full" data-testid="hub-page-content-component">
      <MetaTags
        title={title}
        description="THE BUSINESS TIMES Hub - Find Hub News & Headlines, insight and analysis in Singapore, Asia-Pacific & global markets news at The Business Times."
        ogType="website"
        slug={RouteFactory.hub}
        robots="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />

      <Advertisement
        rootClassName="bg-gray-250 py-6 flex items-center justify-center"
        adUnitProps={{
          type: AdvertisementTypeEnum.LB1,
          slot: GoogleAdsSlotFactory.lb1(adSlotPath),
        }}
      />

      <PrestitialAdWrapper
        {...{
          isPrestitialEnabled,
          topOverlayImpressions,
          topOverlayValidity,
          slot: GoogleAdsSlotFactory.prestitial(adSlotPath),
        }}
      />

      <Header />

      <Container rootClassName="pt-6">
        <Row>
          <Column rootClassName="w-full">
            <h1
              className={cn(
                "mb-2 border-b border-gray-850 pb-3 pt-6 font-playfair text-6xl font-semibold text-gray-850",
                "md:text-8xl"
              )}
              aria-label="CONTENT HUB"
            >
              <span className="text-8xl md:text-10xl">C</span>ONTENT{" "}
              <span className="text-8xl md:text-10xl">H</span>UB
            </h1>
          </Column>

          <>
            {sponsoredContentArticles.length > 0 ? (
              <>
                <Column rootClassName="w-full mt-6">
                  <BlockTitle
                    text="SPONSORED CONTENT"
                    rootClassName="text-lg antialiased md:mb-4"
                  />
                </Column>

                <Column rootClassName="w-full lg:w-2/3 mb-6">
                  <HubSponsoredContentArticles
                    articles={sponsoredContentArticles}
                  />
                </Column>

                <Column rootClassName="w-full lg:w-1/3 mb-6">
                  <Advertisement
                    rootClassName="bg-gray-250 py-6 flex items-center justify-center border-y border-gray-175 sticky top-[70px]"
                    adUnitProps={{
                      type: AdvertisementTypeEnum.IMU1,
                      slot: GoogleAdsSlotFactory.imu1(adSlotPath),
                    }}
                  />
                </Column>
              </>
            ) : null}
          </>
        </Row>

        <Row rootClassName="my-6">
          <Column rootClassName="w-full order-2 lg:order-1 lg:w-2/3 mb-14">
            <HubBrandedContentArticles
              articles={brandedContentArticles}
              className={cn({
                "border-t border-gray-850 pt-2":
                  sponsoredContentArticles.length > 0,
              })}
            />
          </Column>

          <Column rootClassName="w-full flex flex-col order-1 lg:order-2 lg:w-1/3">
            <div className="sticky top-[70px] flex flex-col">
              {sponsoredContentArticles.length === 0 ? (
                <Advertisement
                  rootClassName="bg-gray-250 py-6 flex items-center justify-center mb-14 order-3 lg:order-1 border-y border-gray-175"
                  adUnitProps={{
                    type: AdvertisementTypeEnum.IMU1,
                    slot: GoogleAdsSlotFactory.imu1(adSlotPath),
                  }}
                />
              ) : null}

              <HubPropertySupplementsArticles
                articles={propertySupplementsArticles}
                className={cn("mb-14", {
                  "border-t border-gray-850 pt-2":
                    sponsoredContentArticles.length > 0,
                  "order-1 lg:order-2": sponsoredContentArticles.length === 0,
                })}
              />

              <HubWealthSupplementsArticles
                articles={wealthSupplementsArticles}
                className={cn("mb-14 border-t border-gray-850 pt-2", {
                  "order-2 lg:order-3": sponsoredContentArticles.length === 0,
                })}
              />
            </div>
          </Column>
        </Row>
      </Container>
    </div>
  );
}
